import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, X, Send, MessageSquare, CheckCircle2, Search, ChevronRight, Headphones, Mail, Clock, AlertTriangle, Upload, Paperclip, Camera, ExternalLink } from 'lucide-react';
import useSupportTickets from '../hooks/useSupportTickets';
 
const STATUS_STYLES = {
  Open: 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  Resolved: 'bg-emerald-100 text-emerald-700',
};
 
const PRIORITY_STYLES = {
  Critical: 'bg-red-100 text-red-700',
  High: 'bg-orange-100 text-orange-700',
  Medium: 'bg-yellow-100 text-yellow-700',
  Low: 'bg-gray-100 text-gray-600',
};
 
const CATEGORIES = [
  'Assessment Bug',
  'Content Playback',
  'Login/SSO',
  'Certificate Download',
  'Payment Issue',
  'AI Assistant',
  'Other',
];
 
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
 
const FAQS = [
  {
    q: 'How do I attempt a timed Mock Test?',
    a: 'Go to "Test Series" from the sidebar, choose your exam category, select a test and click "Start Test". You can pause or submit at any time.',
  },
  {
    q: 'Where can I see detailed solution explanations?',
    a: 'After submitting any test, open "Results" and click "View Detailed Analytics" to see step-by-step explanations for every question.',
  },
  {
    q: 'How do I reset my password?',
    a: 'On the login page, click "Forgot password?" and follow the link sent to your registered email. If you don’t receive it within a few minutes, check spam or file a ticket here.',
  },
  {
    q: 'How do I upgrade my subscription plan?',
    a: 'Go to Subscriptions from your account menu and click "Upgrade" to see available plans and pricing.',
  },
  {
    q: 'Can I use the platform on mobile devices?',
    a: 'Yes — the app is fully responsive on phones, tablets, and desktops. Your progress syncs automatically across devices.',
  },
];
 
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
 
// Captures the app content as a PNG, explicitly skipping the support modal's own DOM
// subtree (tagged with data-support-modal-root below) so the screenshot shows what the
// user was looking at, not this modal sitting on top of it.
async function captureScreenshot() {
  const { default: html2canvas } = await import('html2canvas');
  const target = document.getElementById('root');
  if (!target) return null;
  const canvas = await html2canvas(target, {
    logging: false,
    useCORS: true,
    scale: 1,
    ignoreElements: (el) => Boolean(el.closest && el.closest('[data-support-modal-root]')),
  });
  const dataUrl = canvas.toDataURL('image/png');
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
}

function SupportDrawer({ open, onClose, viewAllPath }) {
  const navigate = useNavigate();
  const { tickets, loading, error, fetchTickets, addComment, createTicket } = useSupportTickets({ autoFetch: open });

  const [tab, setTab] = useState('history'); // 'history' | 'new' | 'help'
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [ticketSearch, setTicketSearch] = useState('');
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [screenshot, setScreenshot] = useState(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [titleError, setTitleError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [priorityError, setPriorityError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const [faqSearch, setFaqSearch] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const switchTab = async (nextTab) => {
    setTab(nextTab);
    if (nextTab === 'new' && !screenshot && !capturingScreenshot) {
      setCapturingScreenshot(true);
      try {
        const shot = await captureScreenshot();
        setScreenshot(shot);
      } catch (err) {
        // screenshot capture is best-effort; skip silently on failure (e.g. tainted canvas)
      } finally {
        setCapturingScreenshot(false);
      }
    }
  };
  const resetForm = () => {
    setTitle('');
    setCategory('');
    setPriority('Medium');
    setDescription('');
    setFiles([]);
    setScreenshot(null);
    setSubmitError(null);
    setTitleError(false);
    setCategoryError(false);
    setPriorityError(false);
    setDescriptionError(false);
  };
  // Closing without submitting should discard the draft, not leave it sitting around for
  // whenever the learner reopens the drawer.
  const handleClose = () => {
    if (submitting) return;
    resetForm();
    onClose();
  };
  const handleTicketClick = (ticketId) => {
    setExpandedTicketId(expandedTicketId === ticketId ? null : ticketId);
    setReplyText('');
  };
  const handleReply = async (ticketId) => {
    if (!replyText.trim()) return;
    setReplyLoading(true);
    try {
      await addComment(ticketId, replyText);
      setReplyText('');
    } catch (err) {
      // silently fail or show inline error
    } finally {
      setReplyLoading(false);
    }
  };
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    const missingTitle = !trimmedTitle;
    const missingDescription = !trimmedDescription;
    const missingCategory = !category;
    const missingPriority = !priority;
    setTitleError(missingTitle);
    setDescriptionError(missingDescription);
    setCategoryError(missingCategory);
    setPriorityError(missingPriority);
    if (missingTitle || missingDescription || missingCategory || missingPriority) {
      setSubmitError('Please fill in all required fields before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const allFiles = screenshot ? [...files, screenshot] : files;
      await createTicket({ title: trimmedTitle, category, priority, description: trimmedDescription, files: allFiles });
      setSubmitSuccess(true);
      setTimeout(() => {
        setSubmitSuccess(false);
        setTab('history');
        resetForm();
      }, 1500);
    } catch (err) {
      setSubmitError('Failed to submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  const goToFullPage = () => {
    onClose();
    navigate(viewAllPath);
  };

  const filteredFaqs = FAQS.filter(
    (faq) => faq.q.toLowerCase().includes(faqSearch.toLowerCase()) || faq.a.toLowerCase().includes(faqSearch.toLowerCase())
  );

  const filteredTickets = (tickets || []).filter((ticket) => {
    const matchesStatus = ticketStatusFilter === 'all' || ticket.status === ticketStatusFilter;
    const query = ticketSearch.trim().toLowerCase();
    const matchesSearch = !query
      || ticket.ticket_id?.toLowerCase().includes(query)
      || ticket.title?.toLowerCase().includes(query);
    return matchesStatus && matchesSearch;
  });

  if (!open) return null;

  return (
    <div data-support-modal-root className="fixed inset-0 z-[1300] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl border border-slate-200 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 p-5 sm:p-6 text-white relative flex flex-col gap-3 shrink-0">
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors border border-white/20"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-white/15 border border-white/30 flex items-center justify-center text-white shrink-0 shadow-inner">
              <HelpCircle size={22} />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-black text-white tracking-tight">Need Help &amp; We're here!</h3>
              <p className="text-[11px] sm:text-xs text-blue-100/90 font-medium">
                We're here to help you with your learning journey!
              </p>
            </div>
          </div>
          {/* Navigation Tabs */}
          <div className="flex bg-black/20 p-1 rounded-2xl border border-white/15 mt-1">
            <button
              type="button"
              onClick={() => switchTab('history')}
              className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${tab === 'history' ? 'bg-white text-blue-700 shadow-sm font-extrabold' : 'text-blue-100 hover:text-white hover:bg-white/10'
                }`}
            >
              <MessageSquare size={14} />
              <span>My Requests</span>
            </button>
            <button
              type="button"
              onClick={() => switchTab('new')}
              className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${tab === 'new' ? 'bg-white text-blue-700 shadow-sm font-extrabold' : 'text-blue-100 hover:text-white hover:bg-white/10'
                }`}
            >
              <Send size={14} />
              <span>Ask for Help</span>
            </button>
            {/* <button
              type="button"
              onClick={() => switchTab('help')}
              className={`flex-1 py-2 px-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                tab === 'help' ? 'bg-white text-blue-700 shadow-sm font-extrabold' : 'text-blue-100 hover:text-white hover:bg-white/10'
              }`}
            >
              <LifeBuoy size={14} />
              <span>Get Help</span>
            </button> */}
          </div>
        </div>
        {/* Body */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-5 bg-slate-50/50">
          {tab === 'history' && (
            <div className="text-left">
              {loading && (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                </div>
              )}
              {!loading && error && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
                  <p className="text-sm text-slate-600 mb-3">{error}</p>
                  <button type="button" onClick={fetchTickets} className="text-sm text-blue-600 font-bold">Retry</button>
                </div>
              )}
              {!loading && !error && !tickets.length && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <MessageSquare className="w-10 h-10 text-slate-300 mb-3" />
                  <h3 className="text-sm font-black text-slate-800 mb-1">No Requests yet</h3>
                  <p className="text-xs text-slate-500 mb-4">Create a request to get help from our support team.</p>
                  <button
                    type="button"
                    onClick={() => switchTab('new')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 inline-flex items-center gap-2"
                  >
                    <Send size={14} />
                    New Request
                  </button>
                </div>
              )}
              {!loading && !error && tickets.length > 0 && (
                <div className="space-y-3">
                  {/* Search + status filter */}
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={ticketSearch}
                      onChange={(e) => setTicketSearch(e.target.value)}
                      placeholder="Search by ticket ID or title..."
                      className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                  <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 text-[11px] font-bold">
                    {['all', 'Open', 'In Progress', 'Resolved'].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setTicketStatusFilter(s)}
                        className={`flex-1 py-1.5 rounded-lg transition-all ${ticketStatusFilter === s ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900'
                          }`}
                      >
                        {s === 'all' ? 'All' : s}
                      </button>
                    ))}
                  </div>
                  {!filteredTickets.length && (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <Search className="w-8 h-8 text-slate-300 mb-2" />
                      <p className="text-xs text-slate-500">No tickets match your search/filter.</p>
                    </div>
                  )}
                  {filteredTickets.map((ticket) => (
                    <div key={ticket.ticket_id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                      <button
                        type="button"
                        onClick={() => handleTicketClick(ticket.ticket_id)}
                        className="w-full text-left p-3.5 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] text-slate-400 font-mono">#{ticket.ticket_id}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLES[ticket.status] || 'bg-slate-100 text-slate-600'}`}>
                            {ticket.status === 'Open' && <Clock className="w-2.5 h-2.5 mr-1" />}
                            {ticket.status === 'In Progress' && <AlertTriangle className="w-2.5 h-2.5 mr-1" />}
                            {ticket.status === 'Resolved' && <CheckCircle2 className="w-2.5 h-2.5 mr-1" />}
                            {ticket.status}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${PRIORITY_STYLES[ticket.priority] || 'bg-slate-100 text-slate-600'}`}>
                            {ticket.priority}
                          </span>
                        </div>
                        <h4 className="text-sm font-extrabold text-slate-800 truncate">{ticket.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-slate-400">{ticket.category}</span>
                          <span className="text-[10px] text-slate-300">•</span>
                          <span className="text-[10px] text-slate-400">{formatDate(ticket.created_at)}</span>
                        </div>
                      </button>
                      {expandedTicketId === ticket.ticket_id && (
                        <div className="border-t border-slate-100 bg-slate-50/70 p-3">
                          <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                            {ticket.comments && ticket.comments.length > 0 ? (
                              ticket.comments.map((comment, idx) => (
                                <div
                                  key={idx}
                                  className={`p-2 rounded-xl text-xs ${comment.is_staff ? 'bg-blue-50 border border-blue-100' : 'bg-white border border-slate-200'}`}
                                >
                                  <div className="flex items-center justify-between mb-0.5">
                                    <span className="font-bold text-slate-800 text-[10px]">
                                      {comment.is_staff ? 'Support Team' : 'You'}
                                    </span>
                                    <span className="text-[10px] text-slate-400">{formatDate(comment.created_at)}</span>
                                  </div>
                                  <p className="text-slate-600">{comment.message}</p>
                                </div>
                              ))
                            ) : (
                              <p className="text-xs text-slate-400 text-center py-1">No comments yet.</p>
                            )}
                          </div>
                          {ticket.status !== 'Resolved' && (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleReply(ticket.ticket_id); }}
                                placeholder="Type your reply..."
                                className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                              />
                              <button
                                type="button"
                                onClick={() => handleReply(ticket.ticket_id)}
                                disabled={replyLoading || !replyText.trim()}
                                className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                <Send className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {tab === 'new' && (
            submitSuccess ? (
              <div className="py-8 text-center space-y-4">
                <div className="h-16 w-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-sm">
                  <CheckCircle2 size={36} />
                </div>
                <div className="space-y-1">
                  <h4 className="text-xl font-black text-slate-800">Ticket Submitted!</h4>
                  <p className="text-xs text-slate-500 max-w-sm mx-auto leading-relaxed">
                    Our support team will get back to you soon.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmitTicket} className="space-y-5 text-left">
                <div className="space-y-2">
                  <label htmlFor="drawer-ticket-title" className="block text-xs font-black tracking-wider text-slate-500">
                    What do you need help with ? <span className="text-red-500">*</span>
                    <input
                      id="drawer-ticket-title"
                      type="text"
                      value={title}
                      onChange={(e) => {
                        setTitle(e.target.value);
                        if (titleError) setTitleError(false);
                      }}
                      placeholder="Tell us briefly what went wrong"
                      className={`mt-2 w-full p-3 bg-white border rounded-2xl text-xs font-normal normal-case tracking-normal text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all ${titleError ? 'border-red-300 bg-red-50/50' : 'border-slate-200'
                        }`}
                    />
                  </label>
                  {titleError && <p className="text-[11px] text-red-600 font-bold normal-case">Please enter a title.</p>}
                </div>
                <div className="space-y-2">
                  <span className="block text-xs font-black uppercase tracking-wider text-slate-500">How can we help ? <span className="text-red-500">*</span></span>
                  <div className={`flex flex-wrap gap-2 p-2 -m-2 rounded-2xl border ${categoryError ? 'border-red-300 bg-red-50/50' : 'border-transparent'}`}>
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => {
                          setCategory(cat);
                          setCategoryError(false);
                        }}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${category === cat
                            ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                          }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                  {categoryError && <p className="text-[11px] text-red-600 font-bold">Please select a category.</p>}
                </div>
                <div className="space-y-2">
                  <span className="block text-xs font-black uppercase tracking-wider text-slate-500">How Urgent is this ? <span className="text-red-500">*</span></span>
                  <div className={`flex flex-wrap gap-2 p-2 -m-2 rounded-2xl border ${priorityError ? 'border-red-300 bg-red-50/50' : 'border-transparent'}`}>
                    {PRIORITIES.map((pri) => (
                      <button
                        key={pri}
                        type="button"
                        onClick={() => {
                          setPriority(pri);
                          setPriorityError(false);
                        }}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${priority === pri
                            ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                          }`}
                      >
                        {pri}
                      </button>
                    ))}
                  </div>
                  {priorityError && <p className="text-[11px] text-red-600 font-bold">Please select a priority.</p>}
                </div>
                <div className="space-y-2">
                  <label htmlFor="drawer-ticket-description" className="block text-xs font-black uppercase tracking-wider text-slate-500">
                    Tell us more <span className="text-red-500">*</span>
                    <textarea
                      id="drawer-ticket-description"
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                        if (descriptionError) setDescriptionError(false);
                      }}
                      rows={4}
                      placeholder="Tell us what happened. You can also mention the steps that caused the issue."
                      className={`mt-2 w-full p-3.5 bg-white border rounded-2xl text-xs font-normal normal-case tracking-normal text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none ${descriptionError ? 'border-red-300 bg-red-50/50' : 'border-slate-200'
                        }`}
                    />
                  </label>
                  {descriptionError && <p className="text-[11px] text-red-600 font-bold normal-case">Please enter a description.</p>}
                </div>
                {/* Auto-captured screenshot */}
                <div className="space-y-2">
                  <span className="block text-xs font-black uppercase tracking-wider text-slate-500">Screenshot</span>
                  {capturingScreenshot && (
                    <div className="flex items-center gap-2 text-xs text-slate-500 px-3 py-2.5 border border-dashed border-slate-300 rounded-2xl bg-white">
                      <Camera className="w-3.5 h-3.5 animate-pulse" />
                      Capturing current screen...
                    </div>
                  )}
                  {!capturingScreenshot && screenshot && (
                    <div className="relative inline-block">
                      <img
                        src={URL.createObjectURL(screenshot)}
                        alt="Auto-captured screenshot"
                        className="w-full max-h-32 object-cover rounded-2xl border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => setScreenshot(null)}
                        className="absolute top-1.5 right-1.5 p-1 bg-black/60 text-white rounded-full hover:bg-black/80"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {!capturingScreenshot && !screenshot && (
                    <p className="text-[11px] text-slate-400">No screenshot attached.</p>
                  )}
                </div>
                {/* Manual attachments */}
                <div className="space-y-2">
                  <span className="block text-xs font-black uppercase tracking-wider text-slate-500">Attachments</span>
                  <label htmlFor="drawer-ticket-attachments" className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 cursor-pointer hover:border-slate-300 transition-all">
                    <Upload className="w-3.5 h-3.5" />
                    Choose Files
                    <input id="drawer-ticket-attachments" type="file" multiple onChange={handleFileChange} className="hidden" />
                  </label>
                  {files.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {files.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between px-2.5 py-1.5 bg-white border border-slate-200 rounded-xl">
                          <div className="flex items-center gap-2 min-w-0">
                            <Paperclip className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            <span className="text-[11px] text-slate-600 truncate">{file.name}</span>
                          </div>
                          <button type="button" onClick={() => removeFile(idx)} className="p-0.5 text-slate-400 hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {submitError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-2xl">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-xs text-red-600">{submitError}</p>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting || capturingScreenshot}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-extrabold text-xs rounded-2xl shadow-sm transition-all flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <span>Submitting...</span>
                  ) : (
                    <>
                      <Send size={15} />
                      <span>Submit Request</span>
                    </>
                  )}
                </button>
              </form>
            )
          )}
          {tab === 'help' && (
            <div className="space-y-5 text-left">
              {/* Search Bar */}
              <div className="relative">
                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={faqSearch}
                  onChange={(e) => setFaqSearch(e.target.value)}
                  placeholder="Search help articles & FAQs..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                />
              </div>
              {/* FAQs Accordion */}
              <div className="space-y-2">
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 flex items-center justify-between">
                  <span>Frequently Asked Questions</span>
                  <span className="text-[10px] font-normal text-slate-400">({filteredFaqs.length} results)</span>
                </h4>
                <div className="space-y-2">
                  {filteredFaqs.map((faq, idx) => {
                    const isExpanded = expandedFaq === idx;
                    return (
                      <div key={faq.q} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                        <button
                          type="button"
                          onClick={() => setExpandedFaq(isExpanded ? null : idx)}
                          className="w-full p-3.5 text-left flex items-center justify-between text-xs font-bold text-slate-800 hover:text-blue-600 transition-colors"
                        >
                          <span>{faq.q}</span>
                          <ChevronRight size={15} className={`text-slate-400 transition-transform flex-shrink-0 ml-2 ${isExpanded ? 'rotate-90 text-blue-600' : ''}`} />
                        </button>
                        {isExpanded && (
                          <div className="px-3.5 pb-3.5 text-xs text-slate-600 leading-relaxed border-t border-slate-100 pt-2.5 bg-slate-50/50">
                            {faq.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!filteredFaqs.length && (
                    <p className="text-xs text-slate-400 text-center py-4">No FAQs match your search.</p>
                  )}
                </div>
              </div>
              {/* Direct Support Options */}
              <div className="border-t border-slate-200 pt-4 space-y-2.5">
                <h4 className="text-xs font-black uppercase tracking-wider text-slate-500">Need Direct Assistance?</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <a
                    href="mailto:contact@keyskillset.com"
                    className="p-3 bg-white border border-slate-200 rounded-2xl flex items-center gap-3 shadow-sm hover:border-blue-300 transition-colors"
                  >
                    <div className="h-9 w-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                      <Headphones size={18} />
                    </div>
                    <div className="text-left">
                      <span className="block text-xs font-bold text-slate-800">Student Helpdesk</span>
                      <span className="text-[10px] text-slate-500">contact@keyskillset.com</span>
                    </div>
                  </a>
                  <button
                    type="button"
                    onClick={() => switchTab('new')}
                    className="p-3 bg-white border border-slate-200 rounded-2xl flex items-center gap-3 shadow-sm text-left hover:border-blue-300 transition-colors"
                  >
                    <div className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                      <Mail size={18} />
                    </div>
                    <div>
                      <span className="block text-xs font-bold text-slate-800">Raise a Ticket</span>
                      <span className="text-[10px] text-emerald-600 font-medium">Get a tracked response</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Footer */}
        <div className="p-3.5 bg-white border-t border-slate-200 text-center shrink-0">
          <button
            type="button"
            onClick={goToFullPage}
            className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-slate-500"
          >
            Need more help ?
            <span className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
              View support page
              <ExternalLink className="w-3 h-3" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
SupportDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  viewAllPath: PropTypes.string.isRequired,
};

export default SupportDrawer;
